import type { DetectedPlatform } from "../lib/platform";
import { siteConfig } from "./site";

export type DownloadItem = {
  id: string;
  label: string;
  description: string;
  href?: string;
  qrCodeImageSrc?: string;
  qrCodeAlt?: string;
  qrCodeHint?: string;
  kind: "primary" | "secondary";
  badge?: "app-store" | "play-store" | "f-droid";
  comingSoon?: boolean;
};

const fdroidRepoQrCode = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
  siteConfig.links.fdroidRepo,
)}`;

export function recommendedDownload(platform: DetectedPlatform): DownloadItem {
  switch (platform) {
    case "macos":
      return {
        id: "sparkle-macos",
        label: "Download for macOS",
        description: "Direct signed DMG via Sparkle channel pointer.",
        href: siteConfig.links.sparkleDmg,
        kind: "primary",
      };
    case "windows":
      return {
        id: "windows-appinstaller",
        label: "Download for Windows",
        description: "Self-hosted MSIX App Installer with background updates.",
        href: siteConfig.links.windowsAppInstaller,
        kind: "primary",
      };
    case "linux":
      return {
        id: "flatpak-linux",
        label: "Install Flatpak beta",
        description: "key.store beta remote for Linux desktops and mobile devices.",
        href: siteConfig.links.flatpakRepo,
        kind: "primary",
      };
    case "ios":
      return {
        id: "releases-mobile",
        label: "View downloads",
        description: "Mobile distribution status lives in releases for now.",
        href: siteConfig.links.releases,
        kind: "primary",
      };
    case "android":
      return {
        id: "android-apk",
        label: "Download Android APK",
        description: "Direct APK for manual installs and Play Console package bootstrap.",
        href: siteConfig.links.androidApk,
        kind: "primary",
      };
    default:
      return {
        id: "releases",
        label: "View all downloads",
        description: "All platforms and distribution channels.",
        href: siteConfig.links.releases,
        kind: "primary",
      };
  }
}

export const downloadInventory: Array<{ heading: string; items: DownloadItem[] }> = [
  {
    heading: "Desktop Apps",
    items: [
      {
        id: "sparkle",
        label: "Sparkle (Direct DMG)",
        description: "Signed macOS DMG from the Sparkle channel pointer",
        href: siteConfig.links.sparkleDmg,
        kind: "primary",
      },
      {
        id: "mac-app-store",
        label: "Mac App Store",
        description: "Sandboxed, notarized, auto-updating",
        kind: "primary",
        badge: "app-store",
        comingSoon: true,
      },
      {
        id: "homebrew",
        label: "Homebrew",
        description: "brew install --cask key-store",
        kind: "secondary",
        comingSoon: true,
      },
      {
        id: "windows-appinstaller",
        label: "Windows App Installer",
        description: "Self-hosted signed MSIX with background updates",
        href: siteConfig.links.windowsAppInstaller,
        kind: "primary",
      },
      {
        id: "windows-store",
        label: "Microsoft Store",
        description: "Auto-updating Windows app",
        kind: "primary",
        comingSoon: true,
      },
      {
        id: "flatpak",
        label: "Flatpak Beta",
        description: "flatpak remote-add --if-not-exists keydotstore",
        href: siteConfig.links.flatpakRepo,
        kind: "primary",
      },
      {
        id: "releases-desktop",
        label: "Direct download",
        description: "Signed builds for macOS, Windows, Linux",
        href: siteConfig.links.releases,
        kind: "secondary",
      },
    ],
  },
  {
    heading: "Mobile Apps",
    items: [
      {
        id: "ios-app-store",
        label: "App Store",
        description: "iOS and iPadOS with autofill extension",
        kind: "primary",
        badge: "app-store",
        comingSoon: true,
      },
      {
        id: "play-store",
        label: "Google Play",
        description: "Android with autofill service",
        href: siteConfig.links.playStore,
        kind: "primary",
        badge: "play-store",
      },
      {
        id: "android-apk",
        label: "Android APK",
        description: "Direct APK (manual install / Play Console first upload)",
        href: siteConfig.links.androidApk,
        kind: "secondary",
      },
      {
        id: "android-aab",
        label: "Android App Bundle (AAB)",
        description: "Direct AAB for Google Play Console uploads",
        href: siteConfig.links.androidAab,
        kind: "secondary",
      },
      {
        id: "f-droid",
        label: "F-Droid Repo",
        description: "Repository index for reproducible FOSS Android builds",
        href: siteConfig.links.fdroidRepo,
        qrCodeImageSrc: fdroidRepoQrCode,
        qrCodeAlt: "QR code for the key.store F-Droid repository URL",
        qrCodeHint: "Scan in F-Droid to add repo",
        kind: "primary",
        badge: "f-droid",
      },
    ],
  },
  {
    heading: "Browser Extensions",
    items: [
      {
        id: "chrome-web-store",
        label: "Chrome Web Store",
        description: "Chrome, Edge, Brave, Arc, and Chromium browsers",
        kind: "primary",
        comingSoon: true,
      },
      {
        id: "firefox-addons",
        label: "Firefox Add-ons",
        description: "Firefox and Firefox-based browsers",
        kind: "primary",
        comingSoon: true,
      },
      {
        id: "safari-extensions",
        label: "Safari Extensions",
        description: "macOS and iOS Safari",
        kind: "primary",
        comingSoon: true,
      },
    ],
  },
  {
    heading: "Source & Builds",
    items: [
      {
        id: "releases",
        label: "Releases",
        description: "All artifacts, checksums, and signatures",
        href: siteConfig.links.releases,
        kind: "secondary",
      },
      {
        id: "repo",
        label: "Source code",
        description: "Build from source, audit the code",
        href: siteConfig.links.repo,
        kind: "secondary",
      },
    ],
  },
];
